import Wizard, { WizardStep } from 'Components/SharedComponents/Wizard'
import { useState, useEffect } from 'react'
import { ChartDraft, DimensionInput } from 'TypeDefs/Objectives'
import { Dimension } from 'Generated/graphql-types'
import { hasDuplicates } from 'TypeDefs/Categories'
import { useAppState } from 'Context/AppContext/AppContext'
import CREATE_CHART from 'Apollo/Mutations/CreateChart'
import { CHART } from 'Apollo/Queries/Chart'
import { useMutation } from '@apollo/client'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import { GOALS } from 'Apollo/Queries/Goals'
import Measurement from './Measurement'
import Categories from './Categories'

type CreateChartWizardProps = {
  isOpen: boolean
  onToggle: (isOpen: boolean) => void
  goalId: number
  goalTitle: string
  isGoalSharedWithFunder?: boolean
  objectiveId?: number
  allDimensions: Dimension[]
}

const METRIC_TYPE = 'TOTAL'
const CALCULATION_TYPE = 'CUMULATIVE'

const CreateChartWizard = ({
  isOpen,
  onToggle,
  goalId,
  goalTitle,
  isGoalSharedWithFunder,
  objectiveId,
  allDimensions,
}: CreateChartWizardProps) => {
  const [unitOfMeasurement, setUnitOfMeasurement] = useState<string>('')
  const [targetValue, setTargetValue] = useState<number | null>(null)
  const [title, setTitle] = useState<string | null>(null)
  const [dimensionInfo, setDimensionInfo] = useState<DimensionInput[]>([])

  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId, role, nonprofitOrgRole },
  } = useAppState()

  const measurementFormId = 'measurement-form'
  const categoriesFormId = 'categories-form'

  const dispatchCreateChartSubmitted = analytics.trackActionCreator(
    goals.Goal_Create_Chart_Submitted
  )

  const dispatchCreateChartCanceled = analytics.trackActionCreator(
    initiatives.Goal_Create_Chart_Wizard_Cancel_Clicked
  )

  const [createChartMutation] = useMutation(CREATE_CHART, {
    refetchQueries: [
      {
        query: CHART,
        variables: { goalId },
      },
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
    ],
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    setDimensionInfo([{ title: unitOfMeasurement }])
  }, [unitOfMeasurement])

  let draft: ChartDraft = {
    goalInfo: {
      goalId,
      reportedMetricGroupId: objectiveId || null,
      unitOfMeasurement,
      metric: METRIC_TYPE,
      target: targetValue || undefined,
    },
    dimensionInfo,
    nonprofitOrgId,
  }

  const createChart = async () => {
    const allRequiredStepsCompleted = steps
      .filter((step) => step.action.isRequired)
      .every((step) => step.action.isCompleted)
    if (allRequiredStepsCompleted) {
      draft = {
        ...draft,
        dimensionInfo: dimensionInfo.filter(
          (dimension) => dimension.title !== ''
        ),
      }
      if (draft.dimensionInfo?.length === 0) {
        draft = {
          ...draft,
          dimensionInfo: [
            { title: draft.goalInfo.unitOfMeasurement || 'Measure' },
          ],
        }
      }
      const chartCategories = dimensionInfo.map((category) => ({
        title: category.title,
      }))
      await createChartMutation({
        variables: {
          data: {
            calculationType: CALCULATION_TYPE,
            chartCategories,
            goalId,
            target: targetValue,
            nonprofitOrgId,
            title,
            unitOfMeasurement,
          },
        },
      })
    }

    dispatchCreateChartSubmitted({
      goalId,
      goalTitle,
      goalUpdatedDate: new Date().toISOString().substring(0, 10),
      isGoalSharedWithFunder,
      role,
      relationship: nonprofitOrgRole,
    })
  }

  const onCancelHandler = (step: number) => {
    onToggle(false)

    dispatchCreateChartCanceled({
      initiativeId: objectiveId,
      goalId,
      lastStepCompleted: step,
    })
  }

  const step1NextHandler = () => {
    analytics.trackActionCreator(initiatives.Goal_Chart_Wizard_Step1_Clicked)({
      role,
      relationship: nonprofitOrgRole,
    })
  }

  const updatedChartWizardSteps = [
    {
      name: 'Measurement',
      hideName: true,
      component: (
        <Measurement
          unitOfMeasurement={unitOfMeasurement}
          setUnitOfMeasurement={setUnitOfMeasurement}
          title={title}
          setTitle={setTitle}
          metric={METRIC_TYPE}
          setTargetValue={setTargetValue}
          targetValue={targetValue}
          formId={measurementFormId}
        />
      ),
      action: {
        name: 'Next',
        formId: measurementFormId,
        isRequired: true,
        isCompleted: !!unitOfMeasurement,
        disabled: !unitOfMeasurement,
        handler: step1NextHandler,
      },
    },
    {
      name: 'Categories',
      hideName: true,
      component: (
        <Categories
          allDimensions={allDimensions}
          dimensionInfo={dimensionInfo}
          setDimensionInfo={setDimensionInfo}
          formId={categoriesFormId}
        />
      ),
      action: {
        name: 'Create Chart',
        formId: categoriesFormId,
        isRequired: dimensionInfo.length > 0,
        isCompleted: !hasDuplicates(dimensionInfo),
        handler: createChart,
      },
    },
  ]

  const steps: WizardStep[] = updatedChartWizardSteps

  return (
    <Wizard
      isOpen={isOpen}
      onCancel={onCancelHandler}
      onToggle={onToggle}
      steps={updatedChartWizardSteps}
      width='768px'
    />
  )
}

export default CreateChartWizard
